// Diaspora customer dashboard — Web Portal v2.
//
// Section order is the design's, and the README calls this the showcase
// screen: navy hero with the flag stripe and four product tiles, in-flight
// tracker, stats strip, family cards, activity, featured care packages.
//
// Every number comes from /recipients, /requests and /storefront/packages.
// Where the design shows a figure we cannot compute yet — lifetime spend,
// which needs the payments ledger — the tile is left out rather than filled
// with a plausible-looking guess.

import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Users, Plus, ShoppingBag, Smartphone, Banknote, GraduationCap,
  Loader2, Package as PackageIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PortalShell } from '../components/shared/PortalShell';
import { Card, EmptyState } from '../components/shared/primitives';
import {
  recipients, requests, carePackages,
  type Recipient, type ServiceRequestWithItems, type CarePackageSummary } from '../lib/api';
import { formatHtgMinor, formatUsdFromHtgMinor } from '../lib/money';
import { airtimeEnabled, moneyEnabled } from '../lib/featureFlags';
import { VerifyEmailNotice } from '../components/shared/VerifyEmailNotice';
import { CUSTOMER_NAV } from './_navs';
import { useCustomerNav } from '../lib/pendingAction';

interface CustomerProfile { full_name?: string; country?: string }

const ACTIVE_STATUSES = new Set([
  'submitted', 'quote_pending', 'quoted', 'customer_approval_pending',
  'payment_pending', 'paid', 'assigned_to_merchant', 'in_progress', 'ready_for_delivery', 'delivered',
]);

/** The design's five-dot tracker, and where each status sits on it. */
const TRACKER_STEPS = ['Quoted', 'Paid', 'Preparing', 'On the way', 'Confirmed'] as const;

const TRACKER_INDEX: Record<string, number> = {
  submitted: 0, quote_pending: 0, quoted: 0, customer_approval_pending: 0, payment_pending: 0,
  paid: 1, assigned_to_merchant: 1,
  in_progress: 2,
  ready_for_delivery: 3, delivered: 3,
  confirmed: 4,
};

export function CustomerDashboard() {
  const { user } = useAuth();
  const cp = (user?.customer_profile ?? {}) as CustomerProfile;
  const firstName = (cp.full_name ?? user?.email ?? 'there').split(' ')[0];

  const recipQ    = useQuery({ queryKey: ['me', 'recipients'], queryFn: recipients.list });
  const requestsQ = useQuery({ queryKey: ['me', 'requests'],   queryFn: () => requests.list() });
  const pkgQ      = useQuery({
    queryKey: ['storefront', 'packages'],
    queryFn: carePackages.list,
    staleTime: 5 * 60 * 1000,
    // The marketing strip already tolerates this failing; a dashboard section
    // is not worth retrying into a spinner that never resolves.
    retry: false,
  });

  const reqs = requestsQ.data ?? [];

  const stats = useMemo(() => {
    const active    = reqs.filter((r) => ACTIVE_STATUSES.has(r.status));
    const confirmed = reqs.filter((r) => r.status === 'confirmed');
    const cities    = new Set((recipQ.data ?? []).map((r) => r.city?.name).filter(Boolean));

    return {
      recipCount: recipQ.data?.length ?? 0,
      cityCount: cities.size,
      activeCount: active.length,
      confirmedCount: confirmed.length,
      inFlightHtg: active.reduce((a, r) => a + (r.cart_subtotal_htg_minor ?? 0), 0),
    };
  }, [recipQ.data, reqs]);

  // The design's "In flight" card follows one order. Pick the furthest along,
  // so it shows what is closest to arriving rather than whatever is newest.
  const inFlight = useMemo(() => {
    const active = reqs.filter((r) => ACTIVE_STATUSES.has(r.status));
    if (active.length === 0) return null;
    return [...active].sort(
      (a, b) => (TRACKER_INDEX[b.status] ?? 0) - (TRACKER_INDEX[a.status] ?? 0),
    )[0];
  }, [reqs]);

  // Badge the Home tab when this role owes an action, so a phone user
  // sees it from any screen rather than only on the right page.
  const nav = useCustomerNav(CUSTOMER_NAV);

  return (
    <PortalShell
      nav={nav}
      activeId="dashboard"
      title="Home"
      subtitle="Your family support, at a glance."
      actions={
        <Link
          to="/customer/requests/new"
          className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-[12px] font-semibold text-white hover:bg-brand-700"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> New request
        </Link>
      }
    >
      <div className="space-y-5">
        {/* Says what is blocked before they hit a 403 at the payment step. */}
        <VerifyEmailNotice />

        <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))]">
          <HeroCard firstName={firstName} />
          <InFlightCard request={inFlight} loading={requestsQ.isLoading} />
        </div>

        <StatsStrip {...stats} />

        <div className="grid gap-5 grid-cols-[repeat(auto-fit,minmax(min(100%,420px),1fr))]">
          <FamilyCard people={recipQ.data ?? []} loading={recipQ.isLoading} cityCount={stats.cityCount} />
          <ActivityCard rows={reqs} loading={requestsQ.isLoading} />
        </div>

        <FeaturedPackages packages={pkgQ.data ?? []} loading={pkgQ.isLoading} />
      </div>
    </PortalShell>
  );
}

// ─── Hero ────────────────────────────────────────────────────

/**
 * The four product lines.
 *
 * `to: null` renders a non-interactive tile. Tuition has no partner signed,
 * and Money has no route in this app yet — so a linked tile would 404. A tile
 * that navigates nowhere is worse than one that plainly says it is not ready.
 */
const PRODUCTS = [
  { key: 'groceries', title: 'Groceries', sub: 'Cart from a local merchant', icon: ShoppingBag,   tint: 'bg-brand-50 text-brand-600',     to: '/customer/requests/new' },
  { key: 'airtime',   title: 'Airtime',   sub: 'Digicel · Natcom',           icon: Smartphone,    tint: 'bg-[#EAF0FE] text-[#063B9E]',   to: '/customer/airtime' },
  { key: 'money',     title: 'Money',     sub: 'Via licensed partner Ria',   icon: Banknote,      tint: 'bg-[var(--color-success-bg)] text-[var(--color-success-fg)]', to: '/customer/money' },
  { key: 'tuition',   title: 'Tuition',   sub: 'Coming soon',                icon: GraduationCap, tint: 'bg-[var(--color-warning-bg)] text-[var(--color-warning-fg)]',     to: null },
] as const;

function HeroCard({ firstName }: { firstName: string }) {
  // Airtime disappears entirely when off (its route does not exist either).
  // Money stays visible but inert: it is a headline product, and a customer
  // should see it is coming rather than wonder whether we do it at all.
  const tiles = PRODUCTS
    .filter((p) => p.key !== 'airtime' || airtimeEnabled())
    .map((p) => (p.key === 'money' && !moneyEnabled()
      ? { ...p, sub: 'Coming soon', to: null }
      : p));

  return (
    <section className="relative overflow-hidden rounded-[var(--radius-hero)] bg-gradient-to-br from-brand-600 to-brand-700 p-7 pb-6 text-white shadow-[0_24px_60px_rgba(11,18,32,0.14)]">
      {/* 50/50 Haiti flag stripe — the only flag reference. */}
      <div aria-hidden className="absolute inset-x-0 top-0 flex h-1">
        <span className="w-1/2 bg-[var(--color-interactive)]" />
        <span className="w-1/2 bg-[var(--color-haiti-red)]" />
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 -top-20 h-[280px] w-[280px] rounded-full"
        style={{ background: 'radial-gradient(circle, rgba(127,160,242,0.28), rgba(127,160,242,0) 70%)' }}
      />

      <div className="relative">
        <p className="text-[11.5px] font-extrabold uppercase tracking-[1.4px] text-[#9FBBF5]">Ayiti · +509</p>
        <h1 className="mt-2.5 text-[26px] md:text-[34px] font-extrabold leading-[1.1] tracking-[-1px]">
          Bonjou, {firstName}
        </h1>

        <div className="mt-5 grid gap-2.5 grid-cols-[repeat(auto-fit,minmax(max(170px,calc(50%-10px)),1fr))]">
          {tiles.map((p) => {
            const body = (
              <>
                <span className={`inline-flex h-[34px] w-[34px] items-center justify-center rounded-[11px] ${p.tint}`}>
                  <p.icon className="h-[17px] w-[17px]" aria-hidden="true" />
                </span>
                <span className="block">
                  <span className="block text-[14px] font-extrabold text-[var(--color-ink)]">{p.title}</span>
                  <span className="mt-0.5 block text-[11.5px] text-[var(--color-secondary-2)]">{p.sub}</span>
                </span>
              </>
            );

            const base = 'flex min-h-[112px] flex-col justify-between gap-3 rounded-[var(--radius-button)] p-3.5 text-left';

            return p.to ? (
              <Link key={p.key} to={p.to} className={`${base} bg-white transition-colors hover:bg-[#EEF3FF]`}>
                {body}
              </Link>
            ) : (
              <div key={p.key} className={`${base} cursor-default bg-white/70`}>
                {body}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── In flight ───────────────────────────────────────────────

function InFlightCard({ request, loading }: { request: ServiceRequestWithItems | null; loading: boolean }) {
  if (loading) {
    return (
      <Card className="flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-[var(--color-muted)]" aria-hidden="true" />
      </Card>
    );
  }

  if (!request) {
    return (
      <Card className="flex flex-col justify-center">
        <EmptyState
          icon={PackageIcon}
          title="Nothing in flight"
          body="Start a request and you'll follow it here, step by step."
          action={<Link to="/customer/requests/new" className="text-[12px] font-semibold text-brand-600 hover:underline">New request →</Link>}
        />
      </Card>
    );
  }

  const step = TRACKER_INDEX[request.status] ?? 0;

  return (
    <section className="flex flex-col rounded-[20px] border border-[var(--color-border)] bg-white p-[22px]">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[12px] font-extrabold uppercase tracking-[0.8px] text-[var(--color-muted)]">In flight</p>
        <span className="rounded-lg bg-brand-50 px-2.5 py-1 text-[11px] font-extrabold text-brand-600">
          {request.status.replace(/_/g, ' ').toUpperCase()}
        </span>
      </div>

      <div className="mt-3.5 flex items-center gap-3">
        <span className="inline-flex h-[52px] w-[52px] flex-none items-center justify-center rounded-[14px] bg-brand-50 text-brand-600">
          <ShoppingBag className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[16px] font-extrabold tracking-[-0.2px] text-[var(--color-ink)]">
            {request.title || `${request.category?.label ?? 'Request'} for ${request.recipient?.full_name?.split(' ')[0] ?? ''}`}
          </p>
          <p className="mt-0.5 truncate text-[12.5px] text-[var(--color-secondary-2)]">
            {request.city?.name ?? '—'} · <span className="font-mono">REQ-{String(request.id).padStart(4, '0')}</span>
          </p>
        </div>
      </div>

      <p className="mt-4 text-[26px] font-extrabold tracking-[-0.8px] text-[var(--color-ink)]">
        {request.cart_subtotal_htg_minor > 0 ? formatHtgMinor(request.cart_subtotal_htg_minor) : '—'}
      </p>
      <p className="text-[13px] text-[var(--color-secondary)]">
        {request.cart_subtotal_htg_minor > 0 ? `≈ ${formatUsdFromHtgMinor(request.cart_subtotal_htg_minor)} · ` : ''}
        {request.cart_item_count} item{request.cart_item_count === 1 ? '' : 's'}
      </p>

      <ol className="mt-4 grid grid-cols-5">
        {TRACKER_STEPS.map((label, i) => {
          const done = i < step;
          const now = i === step;
          return (
            <li key={label} className="flex flex-col gap-1.5">
              <div className="flex items-center">
                <span
                  aria-hidden
                  className={`h-3 w-3 flex-none rounded-full ${done ? 'bg-[var(--color-success-fg)]' : now ? 'bg-[var(--color-interactive)] ring-4 ring-[rgba(20,85,232,0.18)]' : 'bg-[var(--color-tint-2)]'}`}
                />
                {i < TRACKER_STEPS.length - 1 && (
                  <span aria-hidden className={`mx-1 h-[3px] flex-1 rounded-sm ${done ? 'bg-[var(--color-success-fg)]' : 'bg-[var(--color-border)]'}`} />
                )}
              </div>
              <span className={`pr-1.5 text-[11.5px] leading-tight ${done || now ? 'font-extrabold text-[var(--color-ink)]' : 'font-semibold text-[var(--color-muted)]'}`}>
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      <div className="flex-1" />

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-[var(--color-hairline)] pt-4">
        <div>
          <p className="text-[11.5px] text-[var(--color-muted)]">Stage</p>
          <p className="text-[17px] font-extrabold text-[var(--color-ink)]">{TRACKER_STEPS[step]}</p>
        </div>
        <Link
          to={`/customer/requests/${request.id}`}
          className="flex-none whitespace-nowrap rounded-xl border border-[var(--color-border)] bg-white px-4 py-2.5 text-[13px] font-extrabold text-[var(--color-ink)] transition-colors hover:border-[var(--color-interactive)] hover:text-[var(--color-interactive)]"
        >
          Track request
        </Link>
      </div>
    </section>
  );
}

// ─── Stats ───────────────────────────────────────────────────

function StatsStrip({
  recipCount, cityCount, activeCount, inFlightHtg, confirmedCount,
}: {
  recipCount: number; cityCount: number; activeCount: number; inFlightHtg: number; confirmedCount: number;
}) {
  // The design's fourth tile is lifetime spend, which needs the payments
  // ledger. Omitted rather than approximated: a money figure that is close
  // but wrong is worse than one that is absent.
  const tiles = [
    { label: 'Family supported', value: String(recipCount), hint: cityCount ? `Across ${cityCount} ${cityCount === 1 ? 'city' : 'cities'}` : 'No recipients yet' },
    { label: 'In flight', value: inFlightHtg > 0 ? formatUsdFromHtgMinor(inFlightHtg) : '—', hint: `${activeCount} open request${activeCount === 1 ? '' : 's'}` },
    { label: 'Delivered', value: String(confirmedCount), hint: confirmedCount > 0 ? 'All confirmed by code' : 'Nothing delivered yet' },
  ];

  return (
    <section className="grid rounded-[20px] border border-[var(--color-border)] bg-white grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))]">
      {tiles.map((t, i) => (
        <div key={t.label} className={`px-[22px] py-5 ${i ? 'border-l border-[var(--color-hairline)]' : ''}`}>
          <p className="text-[12.5px] font-semibold text-[var(--color-secondary-2)]">{t.label}</p>
          <p className="mt-1.5 text-[28px] font-extrabold tracking-[-0.9px] tabular-nums text-[var(--color-ink)]">{t.value}</p>
          <p className="mt-0.5 truncate text-[12px] text-[var(--color-muted)]">{t.hint}</p>
        </div>
      ))}
    </section>
  );
}

// ─── Family ──────────────────────────────────────────────────

function FamilyCard({ people, loading, cityCount }: { people: Recipient[]; loading: boolean; cityCount: number }) {
  return (
    <section className="rounded-[20px] border border-[var(--color-border)] bg-white p-[22px]">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <h2 className="text-[17px] font-extrabold tracking-[-0.3px] text-[var(--color-ink)]">Your family</h2>
          <p className="mt-0.5 text-[12.5px] text-[var(--color-secondary-2)]">
            {people.length === 0
              ? 'Nobody added yet'
              : `${people.length} recipient${people.length === 1 ? '' : 's'}${cityCount ? ` across ${cityCount} ${cityCount === 1 ? 'city' : 'cities'}` : ''}`}
          </p>
        </div>
        <Link to="/customer/recipients" className="flex-none whitespace-nowrap text-[13px] font-extrabold text-[var(--color-interactive)] hover:underline">
          Manage
        </Link>
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div>
      ) : people.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No recipients yet"
          body="Add a family member so they can receive what you send."
          action={<Link to="/customer/recipients" className="text-[12px] font-semibold text-brand-600 hover:underline">Add a recipient →</Link>}
        />
      ) : (
        <div className="mt-4 grid gap-3 grid-cols-[repeat(auto-fit,minmax(130px,1fr))]">
          {people.slice(0, 8).map((p) => (
            <div key={p.id} className="flex flex-col items-center rounded-2xl border border-[var(--color-hairline)] p-4 pb-3.5 text-center">
              <span className="inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[var(--color-tint-2)] text-[17px] font-extrabold text-brand-600">
                {initialsOf(p.full_name)}
              </span>
              <p className="mt-2.5 truncate w-full text-[13.5px] font-extrabold text-[var(--color-ink)]">{p.full_name.split(' ')[0]}</p>
              <p className="mt-px truncate w-full text-[12px] text-[var(--color-muted)]">
                {p.city?.name ?? '—'}{p.relationship ? ` · ${p.relationship}` : ''}
              </p>
              <Link
                to="/customer/requests/new"
                className="mt-3 w-full rounded-[10px] bg-brand-50 py-2 text-[12.5px] font-extrabold text-brand-600 transition-colors hover:bg-[var(--color-tint-2)]"
              >
                Send
              </Link>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p.charAt(0).toUpperCase()).join('');
}

// ─── Activity ────────────────────────────────────────────────

/**
 * A human sentence plus the state caption the design puts on the right.
 *
 * The previous version rendered the raw status with underscores swapped for
 * spaces, so a customer read "customer approval pending" — our schema, not
 * their situation.
 */
function describe(r: ServiceRequestWithItems): { title: string; state: string; tone: string } {
  const who = r.recipient?.full_name?.split(' ')[0] ?? 'your family';
  switch (r.status) {
    case 'quoted':
    case 'customer_approval_pending':
      return { title: 'Quote ready to approve', state: 'ACTION NEEDED', tone: 'text-[var(--color-warning-fg)]' };
    case 'payment_pending':
      return { title: 'Waiting for payment', state: 'ACTION NEEDED', tone: 'text-[var(--color-warning-fg)]' };
    case 'submitted':
    case 'quote_pending':
      return { title: 'Sent to the merchant', state: 'AWAITING QUOTE', tone: 'text-[var(--color-secondary-2)]' };
    case 'delivered':
      return { title: `Delivered to ${who}`, state: 'AWAITING CODE', tone: 'text-brand-600' };
    case 'confirmed':
      return { title: `${who} confirmed delivery`, state: 'COMPLETED', tone: 'text-[var(--color-success-fg)]' };
    case 'cancelled':
      return { title: 'Request cancelled', state: 'CANCELLED', tone: 'text-[var(--color-muted)]' };
    case 'refunded':
      return { title: 'Refunded', state: 'REFUNDED', tone: 'text-[var(--color-success-fg)]' };
    case 'disputed':
      return { title: 'Problem reported', state: 'PAYOUT HELD', tone: 'text-[var(--color-danger-fg)]' };
    default:
      return { title: 'Merchant is preparing it', state: 'IN PROGRESS', tone: 'text-brand-600' };
  }
}

function ActivityCard({ rows, loading }: { rows: ServiceRequestWithItems[]; loading: boolean }) {
  const recent = [...rows]
    .sort((a, b) => (b.updated_at ?? '').localeCompare(a.updated_at ?? ''))
    .slice(0, 5);

  return (
    <section className="rounded-[20px] border border-[var(--color-border)] bg-white p-[22px]">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-[17px] font-extrabold tracking-[-0.3px] text-[var(--color-ink)]">Activity</h2>
        <Link to="/customer/requests" className="flex-none whitespace-nowrap text-[13px] font-extrabold text-[var(--color-interactive)] hover:underline">
          View all
        </Link>
      </div>

      {loading ? (
        <div className="py-8 text-center"><Loader2 className="inline h-4 w-4 animate-spin text-[var(--color-muted)]" aria-hidden="true" /></div>
      ) : recent.length === 0 ? (
        <EmptyState icon={PackageIcon} title="No activity yet" body="Your orders will appear here as they move." />
      ) : (
        <div className="mt-3 flex flex-col">
          {recent.map((r, i) => {
            const d = describe(r);
            return (
              <Link
                key={r.id}
                to={`/customer/requests/${r.id}`}
                className={`flex items-center gap-3 py-2.5 transition-colors hover:bg-[var(--color-tint-3)] ${i ? 'border-t border-[var(--color-hairline)]' : ''}`}
              >
                <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-[11px] bg-brand-50 text-brand-600">
                  <ShoppingBag className="h-4 w-4" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13.5px] font-bold text-[var(--color-ink)]">{d.title}</span>
                  <span className="mt-px block truncate text-[12px] text-[var(--color-muted)]">
                    {r.category?.label ?? '—'} · REQ-{String(r.id).padStart(4, '0')}
                  </span>
                </span>
                <span className="flex-none text-right">
                  <span className="block text-[13.5px] font-extrabold tabular-nums text-[var(--color-ink)]">
                    {r.cart_subtotal_htg_minor > 0 ? formatUsdFromHtgMinor(r.cart_subtotal_htg_minor) : '—'}
                  </span>
                  <span className={`mt-px block whitespace-nowrap text-[11px] font-extrabold tracking-[0.3px] ${d.tone}`}>
                    {d.state}
                  </span>
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─── Featured care packages ──────────────────────────────────

function FeaturedPackages({ packages, loading }: { packages: CarePackageSummary[]; loading: boolean }) {
  // Nothing to feature is not an error worth a placeholder.
  if (!loading && packages.length === 0) return null;

  return (
    <section className="pt-2">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <h2 className="text-[20px] font-extrabold tracking-[-0.4px] text-[var(--color-ink)]">Care packages</h2>
          <p className="mt-0.5 text-[13px] text-[var(--color-secondary-2)]">
            Ready-made boxes from verified merchants. One tap to send.
          </p>
        </div>
        <Link to="/customer/packages" className="flex-none whitespace-nowrap text-[13px] font-extrabold text-[var(--color-interactive)] hover:underline">
          See all packages
        </Link>
      </div>

      <div className="mt-4 grid gap-4 grid-cols-[repeat(auto-fit,minmax(240px,1fr))]">
        {loading
          ? Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-56 animate-pulse rounded-[20px] border border-[var(--color-hairline)] bg-white" />
            ))
          : packages.slice(0, 3).map((p) => (
              <article key={p.id} className="flex flex-col overflow-hidden rounded-[20px] border border-[var(--color-border)] bg-white">
                {/* Real photography is still outstanding (handoff README,
                    "Still needed from the business"), so this is a branded
                    panel carrying the occasion tag rather than a blank frame. */}
                <div className="relative aspect-[16/10] bg-gradient-to-br from-brand-100 via-[var(--color-tint-3)] to-brand-50">
                  {p.audience && (
                    <span className="absolute left-3 top-3 rounded-md bg-white/90 px-2 py-1 text-[10.5px] font-extrabold uppercase tracking-[0.6px] text-[var(--color-ink)]">
                      {p.audience}
                    </span>
                  )}
                </div>
                <div className="flex flex-1 flex-col p-4 pb-[18px]">
                  <h3 className="text-[16px] font-extrabold tracking-[-0.2px] text-[var(--color-ink)]">{p.name}</h3>
                  <p className="mt-0.5 truncate text-[12.5px] text-[var(--color-secondary-2)]">
                    {p.merchant?.business_name ?? 'Verified merchant'} · {p.items_count} item{p.items_count === 1 ? '' : 's'}
                  </p>
                  <div className="flex-1" />
                  <div className="mt-3.5 flex items-center justify-between gap-2">
                    <span className="text-[18px] font-extrabold tabular-nums text-[var(--color-ink)]">
                      {formatUsdFromHtgMinor(p.approx_htg_minor ?? 0)}
                    </span>
                    <Link
                      to="/customer/packages"
                      className="flex-none rounded-[11px] bg-[var(--color-ink)] px-4 py-2.5 text-[12.5px] font-extrabold text-white transition-colors hover:bg-brand-600"
                    >
                      Send box
                    </Link>
                  </div>
                </div>
              </article>
            ))}
      </div>
    </section>
  );
}
